import React, { useState } from "react";
import { Card, CardContent, Typography, Box, Chip } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import VerifiedIcon from "@mui/icons-material/Verified";
import ResponsiveImage from "./ResponsiveImage";
import { SIZES } from "../../utils/responsiveImage";

const CardTile = ({ image, icon, title, description, onArrowClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Card
      sx={{
        display: "flex",
        alignItems: "stretch",
        width: "100%",
        borderRadius: 5,
        overflow: "visible",
        cursor: "pointer",
        boxShadow: hovered
          ? "0 24px 50px rgba(123,110,102,0.18)"
          : "0 8px 24px rgba(123,110,102,0.08)",
        bgcolor: "#fff",
        position: "relative",
        border: "1px solid",
        borderColor: hovered ? "rgba(201,167,119,0.45)" : "rgba(123,110,102,0.10)",
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        transition:
          "transform .35s cubic-bezier(.2,.8,.2,1), box-shadow .35s ease, border-color .35s ease",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onArrowClick}
    >
      {/* Decorative cream wash on hover */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          background:
            "linear-gradient(135deg, rgba(253,246,234,0.0) 0%, rgba(253,246,234,0.7) 100%)",
          opacity: hovered ? 1 : 0,
          transition: "opacity .35s ease",
          pointerEvents: "none",
        }}
      />

      {/* Left Image */}
      <Box
        sx={{
          width: "40%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 16,
            borderRadius: 3,
            background:
              "linear-gradient(135deg, #fdf6ea 0%, #f7eada 50%, #ecd9b9 100%)",
            opacity: hovered ? 1 : 0.65,
            transition: "opacity .3s ease",
          }}
        />
        <ResponsiveImage
          src={image}
          alt={title}
          sizes={SIZES.cardTile}
          sx={{
            position: "relative",
            zIndex: 2,
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            transform: hovered
              ? "translateY(-12%) scale(1.04)"
              : "translateY(0) scale(1)",
            transition:
              "transform .4s cubic-bezier(.2,.8,.2,1), filter .3s ease",
            filter: hovered
              ? "drop-shadow(12px 16px 18px rgba(123,110,102,0.25))"
              : "drop-shadow(0 6px 12px rgba(123,110,102,0.10))",
            borderRadius: 3,
          }}
        />
      </Box>

      {/* Content */}
      <CardContent
        sx={{
          flex: 1,
          padding: { xs: 2, md: 3 },
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Chip
          icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
          label="Trusted & Regulated"
          size="small"
          sx={{
            alignSelf: "flex-start",
            bgcolor: "#fdf6ea",
            color: "#a1885e",
            fontWeight: 700,
            letterSpacing: 0.4,
            fontSize: "0.7rem",
            height: 22,
            border: "1px solid rgba(201,167,119,0.30)",
            "& .MuiChip-icon": { color: "#a1885e", ml: "6px" },
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mt: 0.5,
          }}
        >
          <Box
            sx={{
              width: { xs: 44, md: 56 },
              height: { xs: 44, md: 56 },
              borderRadius: 3,
              bgcolor: hovered ? "#C9A777" : "#fdf6ea",
              color: hovered ? "#fff" : "#a1885e",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: hovered
                ? "0 8px 18px rgba(201,167,119,0.35)"
                : "none",
              transition: "all .3s ease",
            }}
          >
            {React.cloneElement(icon, {
              sx: { fontSize: { xs: 24, md: 30 } },
            })}
          </Box>
          <Typography
            sx={{
              color: "#3d322b",
              fontWeight: 700,
              fontSize: { xs: "1.25rem", md: "1.6rem" },
              lineHeight: 1.15,
            }}
          >
            {title}
          </Typography>
        </Box>

        <Typography
          sx={{
            color: "#7B6E66",
            fontSize: { xs: "0.85rem", md: "0.95rem" },
            mt: 0.5,
          }}
        >
          {description}
        </Typography>

        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            mt: "auto",
            pt: 1,
            color: hovered ? "#a1885e" : "#9c8e85",
            fontWeight: 700,
            fontSize: "0.85rem",
            letterSpacing: 0.4,
            transition: "color .25s ease",
          }}
        >
          Get a quote in 90s
          <ArrowForward
            sx={{
              fontSize: 16,
              transform: hovered ? "translateX(4px)" : "translateX(0)",
              transition: "transform .25s ease",
            }}
          />
        </Box>
      </CardContent>

      {/* Right Arrow Bubble */}
      <Box
        sx={{
          position: "absolute",
          bottom: { xs: -14, md: -18 },
          right: { xs: -14, md: -18 },
          background:
            "linear-gradient(135deg, #d3b48a 0%, #C9A777 50%, #b4966b 100%)",
          borderRadius: "50%",
          width: { xs: 56, md: 80 },
          height: { xs: 56, md: 80 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: hovered
            ? "0 14px 30px rgba(201,167,119,0.55)"
            : "0 8px 18px rgba(201,167,119,0.35)",
          transition: "transform .35s cubic-bezier(.2,.8,.2,1), box-shadow .3s ease",
          transform: hovered ? "scale(1.08)" : "scale(1)",
          zIndex: 2,
        }}
        onClick={onArrowClick}
      >
        <ArrowForward
          sx={{
            fontSize: { xs: 24, md: 32 },
            color: "#fff",
            transform: hovered ? "translateX(2px)" : "translateX(0)",
            transition: "transform .3s ease",
          }}
        />
      </Box>
    </Card>
  );
};

export default CardTile;
